import type { SeriesPoint } from "./types";

export interface ForecastPoint extends SeriesPoint { lower80: number; upper80: number; lower95: number; upper95: number; status: "MODELO" }
export interface CandidateScore { id: "NAIVE" | "DRIFT" | "HOLT_DAMPED"; label: string; mae: number; rmse: number; smape: number; bias: number }
export interface ForecastResult {
  model: string;
  modelId: CandidateScore["id"];
  mae: number;
  rmse: number;
  smape: number;
  bias: number;
  backtestCount: number;
  sampleSize: number;
  candidates: CandidateScore[];
  forecast: ForecastPoint[];
  simulations: number;
  seed: number;
  intervalMethod: string;
  residualBandwidth: number;
  validationStatus: "PROVISIONAL" | "VALIDADO";
  minimumBacktestsForCalibration: number;
  improvementVsNaivePct: number;
}

const mean = (values: number[]) => values.reduce((a, b) => a + b, 0) / Math.max(1, values.length);
const quantile = (sorted: number[], probability: number) => {
  const position = Math.max(0, Math.min(sorted.length - 1, (sorted.length - 1) * probability));
  const lower = Math.floor(position), upper = Math.ceil(position), weight = position - lower;
  return sorted[lower] * (1 - weight) + sorted[upper] * weight;
};
function seededRandom(seed: number) {
  let state = seed >>> 0;
  return () => { state = (state * 1664525 + 1013904223) >>> 0; return state / 4294967296; };
}
function standardNormal(random: () => number) {
  const u = Math.max(1e-12, random()), v = Math.max(1e-12, random());
  return Math.sqrt(-2 * Math.log(u)) * Math.cos(2 * Math.PI * v);
}
function score(actual: number[], predicted: number[]) {
  const errors = actual.map((value, index) => predicted[index] - value);
  return { mae: mean(errors.map(Math.abs)), rmse: Math.sqrt(mean(errors.map((error) => error ** 2))), smape: mean(actual.map((value, index) => 200 * Math.abs(value - predicted[index]) / Math.max(1e-6, Math.abs(value) + Math.abs(predicted[index])))), bias: mean(errors) };
}
function dampedFit(values: number[], alpha: number, beta: number, phi: number) {
  let level = values[0], trend = values.length > 1 ? values[1] - values[0] : 0;
  for (let index = 1; index < values.length; index++) { const previous = level; level = alpha * values[index] + (1 - alpha) * (level + phi * trend); trend = beta * (level - previous) + (1 - beta) * phi * trend; }
  return { level, trend, alpha, beta, phi };
}
function optimizeDamped(values: number[]) {
  let best = { ...dampedFit(values, .4, .2, .9), sse: Number.POSITIVE_INFINITY };
  for (const alpha of [.2, .4, .6, .8]) for (const beta of [.1, .2, .4]) for (const phi of [.8, .9, .98]) {
    let total = 0;
    for (let index = 3; index < values.length; index++) { const fit = dampedFit(values.slice(0, index), alpha, beta, phi); total += (fit.level + phi * fit.trend - values[index]) ** 2; }
    if (total < best.sse) best = { ...dampedFit(values, alpha, beta, phi), sse: total };
  }
  return best;
}
function predict(id: CandidateScore["id"], train: number[], horizon = 1) {
  if (id === "NAIVE") return train.at(-1)!;
  if (id === "DRIFT") return train.at(-1)! + horizon * (train.at(-1)! - train[0]) / Math.max(1, train.length - 1);
  const fit = optimizeDamped(train);
  const dampedSum = Array.from({ length: horizon }, (_, index) => fit.phi ** (index + 1)).reduce((a, b) => a + b, 0);
  return fit.level + dampedSum * fit.trend;
}
function nextDate(last: string, step: number, frequency: "monthly" | "daily") {
  const date = new Date(last.length === 7 ? `${last}-01T00:00:00Z` : `${last}T00:00:00Z`);
  frequency === "monthly" ? date.setUTCMonth(date.getUTCMonth() + step) : date.setUTCDate(date.getUTCDate() + step);
  return frequency === "monthly" ? date.toISOString().slice(0, 7) : date.toISOString().slice(0, 10);
}
export function monthlyAverage(series: SeriesPoint[]) {
  const buckets = new Map<string, number[]>();
  series.filter((point) => Number.isFinite(point.value)).forEach((point) => { const key = point.date.slice(0, 7); buckets.set(key, [...(buckets.get(key) ?? []), point.value]); });
  return [...buckets.entries()].sort(([a], [b]) => a.localeCompare(b)).map(([date, values]) => ({ date, value: mean(values), status: "REAL" as const }));
}
export function buildForecast(series: SeriesPoint[], horizon: number, frequency: "monthly" | "daily" = "monthly"): ForecastResult {
  const clean = [...series].filter((point) => Number.isFinite(point.value)).sort((a, b) => a.date.localeCompare(b.date));
  const minimum = frequency === "monthly" ? 12 : 30;
  if (clean.length < minimum) throw new Error(`Se requieren al menos ${minimum} observaciones ${frequency === "monthly" ? "mensuales" : "diarias"}`);
  const values = clean.map((point) => point.value), start = Math.max(6, Math.floor(values.length * .55)), actual: number[] = [];
  const predictions: Record<CandidateScore["id"], number[]> = { NAIVE: [], DRIFT: [], HOLT_DAMPED: [] };
  for (let index = start; index < values.length; index++) { const train = values.slice(0, index); actual.push(values[index]); (Object.keys(predictions) as CandidateScore["id"][]).forEach((id) => predictions[id].push(predict(id, train))); }
  const labels: Record<CandidateScore["id"], string> = { NAIVE: "Naive de último valor", DRIFT: "Tendencia con deriva", HOLT_DAMPED: "Holt amortiguado" };
  const candidates = (Object.keys(predictions) as CandidateScore["id"][]).map((id) => ({ id, label: labels[id], ...score(actual, predictions[id]) })).sort((a, b) => a.rmse - b.rmse);
  const winner = candidates[0];
  const residuals = actual.map((value, index) => value - predictions[winner.id][index]);
  const residualMean = mean(residuals);
  const centeredResiduals = residuals.map((error) => error - residualMean);
  const sortedResiduals = [...centeredResiduals].sort((a, b) => a - b);
  const residualSigma = Math.sqrt(mean(centeredResiduals.map((error) => error ** 2)));
  const residualIqr = quantile(sortedResiduals, .75) - quantile(sortedResiduals, .25);
  const robustScale = Math.min(residualSigma || winner.rmse, residualIqr > 0 ? residualIqr / 1.34 : residualSigma || winner.rmse);
  const residualBandwidth = Math.max(1e-9, .9 * robustScale * centeredResiduals.length ** (-.2));
  const lastDate = clean.at(-1)!.date;
  const simulations = 10_000, seed = 2030, random = seededRandom(seed);
  const simulatedErrors = Array.from({ length: horizon }, () => [] as number[]);
  for (let simulation = 0; simulation < simulations; simulation++) {
    let accumulatedError = 0;
    for (let step = 0; step < horizon; step++) {
      const empiricalResidual = centeredResiduals[Math.floor(random() * centeredResiduals.length)];
      accumulatedError += empiricalResidual + residualBandwidth * standardNormal(random);
      simulatedErrors[step].push(accumulatedError);
    }
  }
  simulatedErrors.forEach((errors) => errors.sort((a, b) => a - b));
  const forecast = Array.from({ length: horizon }, (_, index) => {
    const h = index + 1, value = predict(winner.id, values, h), errors = simulatedErrors[index];
    return {
      date: nextDate(lastDate, h, frequency), value,
      lower80: value + quantile(errors, .10), upper80: value + quantile(errors, .90),
      lower95: value + quantile(errors, .025), upper95: value + quantile(errors, .975),
      status: "MODELO" as const
    };
  });
  const naive = candidates.find((candidate) => candidate.id === "NAIVE")!;
  const improvementVsNaivePct = naive.rmse > 0 ? (naive.rmse - winner.rmse) / naive.rmse * 100 : 0;
  const minimumBacktestsForCalibration = 12;
  return {
    model: winner.label, modelId: winner.id, mae: winner.mae, rmse: winner.rmse, smape: winner.smape,
    bias: winner.bias, backtestCount: actual.length, sampleSize: values.length, candidates, forecast,
    simulations, seed, intervalMethod: "Bootstrap residual suavizado y acumulado por horizonte", residualBandwidth,
    validationStatus: actual.length >= minimumBacktestsForCalibration ? "VALIDADO" : "PROVISIONAL",
    minimumBacktestsForCalibration, improvementVsNaivePct
  };
}
